import type { AuthLinkToken } from "./authLinkToken";

const SPENT_LINK_KEY = "auth.spentLink";

/**
 * Fingerprints the token's secret so a spent link stays recognisable across a remount
 * without the secret itself being persisted (djb2).
 *
 * A collision would drop a fresh link through the gate unconfirmed rather than merely
 * re-arming it, but at one stored 32-bit value that is not worth defending against.
 */
const fingerprintToken = (token: AuthLinkToken): string => {
  let secret: string;
  switch (token.type) {
    case "code":
      secret = token.code;
      break;
    case "hash":
      secret = token.accessToken;
      break;
    case "token_hash":
      secret = token.tokenHash;
      break;
  }

  let hash = 5381;
  for (let i = 0; i < secret.length; i++) {
    hash = ((hash << 5) + hash + secret.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
};

/** True when this exact link was already submitted in this tab. */
export const isAuthLinkSpent = (token: AuthLinkToken): boolean => {
  try {
    return window.sessionStorage.getItem(SPENT_LINK_KEY) === fingerprintToken(token);
  } catch (error) {
    // Storage blocked (Safari ITP, partitioned context): the gate re-arms on remount and
    // asks the user to click Continue on an already-consumed token, which dead-ends at
    // "this link is no longer valid". Rare, but worth being able to find in the logs.
    console.error("[spentAuthLink] Could not read spent-link fingerprint:", error);
    return false;
  }
};

export const markAuthLinkSpent = (token: AuthLinkToken): void => {
  try {
    window.sessionStorage.setItem(SPENT_LINK_KEY, fingerprintToken(token));
  } catch (error) {
    console.error("[spentAuthLink] Could not record spent-link fingerprint:", error);
  }
};

/**
 * Forgets a link recorded as spent. Called when the exchange fails: a rejected token was
 * never consumed, so the real link is still live and must stay clickable.
 */
export const forgetAuthLinkSpent = (): void => {
  try {
    window.sessionStorage.removeItem(SPENT_LINK_KEY);
  } catch (error) {
    console.error("[spentAuthLink] Could not clear spent-link fingerprint:", error);
  }
};
